import {
  AfterViewInit,
  Component,
  computed,
  ContentChild,
  effect,
  EffectRef,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  inject,
  Injector,
  Input,
  OnDestroy,
  Output,
  runInInjectionContext,
  signal,
  TemplateRef,
  ViewChild,
  ViewContainerRef
} from '@angular/core';
import { CommonModule, NgFor, NgIf } from '@angular/common';
import { getDocument, GlobalWorkerOptions, PDFDocumentProxy, PDFPageProxy } from 'pdfjs-dist';
import * as L from 'leaflet';
import { LatLng, LatLngBoundsExpression } from 'leaflet';
import { GestureHandling } from 'leaflet-gesture-handling';
import '@geoman-io/leaflet-geoman-free';
import { OptionalContentConfig } from 'pdfjs-dist/types/src/display/optional_content_config';
import { convertPdfPageToDataURL, createPdfPageBackdrop, PdfLayerGroup } from './pdf-handling.lib';
import { injectChangeDetectorRef, injectDestroyRef } from '@qpoint/utils';
import { range, sum } from 'lodash';
import {
  auditTime,
  BehaviorSubject,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  filter,
  fromEvent,
  map,
  merge,
  startWith,
  Subject
} from 'rxjs';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { QPointPagedMultiPolygon } from './qpoint-paged-multi-polygon';
import { ControlOpacity } from './controls/control-opacity';
import { ControlBar } from './controls/control-bar';
import { ControlPdfLayer } from './controls/control-pdf-layer';
import { ControlPagination } from './controls/control-pagination';
import { ControlFullscreen } from './controls/control-fullscreen';
import { QPointIconModule } from '@qpoint/icons';
import { QPointShowIfTruncatedDirective } from '@qpoint/layout';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { QPointLoadingDirective } from '@qpoint/loading';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { DrawingLocalStorage, getDrawingLocalStorage, saveDrawingLocalStorage } from './drawing-local-storage';
import { PopupRef, PopupService } from '@progress/kendo-angular-popup';
import { QPointPolygonPopUpComponent } from './components/polygon-pop-up/polygon-pop-up.component';
import { ScaleDialogComponent, ScaleDialogData, ScaleDialogTextConfig } from './components/scale-dialog/scale-dialog.component';
import { getScaleControlOptions } from './controls/control-scale';
import { getRenderedIcon } from './controls/helpers';
import { faArrowPointer, faDrawPolygon, faHand, faRedo, faRulerVertical, faUndo } from '@fortawesome/pro-regular-svg-icons';
import {
  QPointPolygonPopUpScalingFactorRequiredTemplateDirective
} from './components/polygon-pop-up/polygon-pop-up-scaling-factor-required-template.directive';
import { IconDefinition } from '@fortawesome/pro-light-svg-icons';
import { getUndoControlOptions } from './controls/control-undo';
import { getRedoControlOptions } from './controls/control-redo';
import { getPanControlOptions } from './controls/control-pan';
import { takeUntil } from 'rxjs/operators';

export interface PolygonChange {
  id: number,
  coordinates: L.LatLng[]
}

export type ControlTextConfig = {
  controlLayersTitle: string
  controlLayerAllLayersToggle: string
}

export type ViewConfig = {
  zoom: number,
  center: L.LatLng
}

export interface DrawingRightClickEvent {
  x: number,
  y: number
}

export interface DrawingPolygonRightClickEvent extends DrawingRightClickEvent {
  polygonId: number,
}

export const DEFAULT_ZOOM_GRANULARITY = 2;

@Component({
    selector: 'qpoint-drawing',
    imports: [
      CommonModule, 
      NgIf, 
      NgFor, 
      QPointIconModule, 
      QPointShowIfTruncatedDirective, 
      NgbTooltip, 
      QPointLoadingDirective, 
      FontAwesomeModule, 
      ScaleDialogComponent
    ],
    templateUrl: './drawing.component.html',
    styleUrls: ['./drawing.component.scss']
})
export class QPointDrawingComponent implements AfterViewInit, OnDestroy {
  private destroyRef = injectDestroyRef();
  private cdr = injectChangeDetectorRef();
  private injector = inject(Injector);
  private viewConfigs = new Map<number, ViewConfig>();
  private createMapTrigger$ = new Subject<void>();

  private pdfScale = 3;
  private pages: PDFPageProxy[] = [];
  private optionalConfig: OptionalContentConfig | undefined = undefined;
  private initialized = false;
  private currentPdfDoc: PDFDocumentProxy | undefined = undefined;
  private paginationUpdateEffect: EffectRef | undefined = undefined;
  protected readonly controlPdfLayerId = `control-pdf-layer-${crypto.randomUUID()}`;
  protected readonly controlPaginationId = `control-pagination-${crypto.randomUUID()}`;
  private _scale = signal<ScaleDialogData | null>(null);
  scale = this._scale.asReadonly();
  scalingFactor = computed(() => {
    const scale = this.scale();

    if (scale && scale.realLength && scale.referenceLength) {
      return scale.realLength / scale.referenceLength;
    } else {
      return null;
    }
  });
  private topRightControlBar: ControlBar = null;
  public selectedPolygon = signal(null);
  public selectedVertex = signal(null);
  private mouseCursorOnPolygon = false;
  private mouseCursorOnVirtualVertex = false;
  private elementClicked = false;
  private manuallyUpdatedPolygons = false;
  private scaleLineLayer: L.Layer | undefined = undefined;
  private _undoDisabled = true;
  private _redoDisabled = true;
  private lastOffsetTop = null;
  private lastOffsetLeft = null;
  private resizeObserver: ResizeObserver;
  private cbAfterOffsetChanges = [() => {
    this.popupRef?.close();
    this.selectedPolygon() ? this.openPolygonPopupEditMode(this.selectedPolygon()) : null;
  }];

  numOfPages = signal(0);
  mappedPages = signal<{ hasPolygon: boolean, index: number }[]>([]);
  selectedIndex = signal(0); // Selected page index. Starts at 0
  layers = signal<Record<string, PdfLayerGroup> | null>(null);

  allLayersVisible = computed(() => {
    const layers = this.layers();

    if (!layers || Object.keys(layers).length === 0) {
      return true;
    }

    return Object.values(layers).every(layer => layer.visible);
  });
  loading = signal(false);

  private map: L.Map;
  @ViewChild('map') mapRef: ElementRef;
  @ViewChild('map', {read: ViewContainerRef}) mapContainerRef: ViewContainerRef;
  @ViewChild(ScaleDialogComponent) scaleDialogComponent: ScaleDialogComponent;
  @ViewChild(ScaleDialogComponent, {read: ViewContainerRef}) scaleDialogViewContainerRef: ViewContainerRef;
  @ContentChild(QPointPolygonPopUpScalingFactorRequiredTemplateDirective, {read: TemplateRef}) scalingFactorRequiredTemplate: TemplateRef<any>;

  @Input() set src(url: string) {
    this._src = url;
    if (this.initialized) {
      void this.loadFileForLeaflet();
    }
  }

  private _src: string;
  @Input() type = 'pdf';

  private _isDrawingEnabled: boolean;
  @Input() set isDrawingEnabled(value: boolean) {
    this._isDrawingEnabled = value;
    if (!this._isDrawingEnabled && this.map) {
      // leave edit mode
      this.map.pm.disableGlobalEditMode();
      this.map.pm.disableGlobalDragMode();
      this.map.pm.disableGlobalCutMode();
      this.map.pm.disableGlobalRemovalMode();
      this.map.dragging.enable();
      if (this.selectedPolygon()) {
        this.deselectPolygon();
      }
    }

    if (this._isDrawingEnabled && this.map) {
      this.map.pm.enableGlobalEditMode();
      this.disableAllExceptSelectedPolygons();
    }

    if (this._isDrawingEnabled && this.map && this.map.dragging.enabled()) {
      this.map.dragging.disable();
    }

    this.checkAndClosePopupRef(false);
    this.handleControlVisibility();
  }

  public get isDrawingEnabled() {
    return this._isDrawingEnabled;
  }

  @Input() language: L.PM.SupportLocales = 'de';

  private _initialPage: number = null;
  /**
   * The initial page to show. The page number starts at 1.+
   */
  @Input() set initialPage(value: number) {
    if (isNaN(value) || !value) {
      value = 1;
    }
    this._initialPage = value;
    this.selectedIndex.set(this._initialPage - 1);

    if (this.map) {
      if (this.pages) {
        const pageIndex = this._initialPage ? this._initialPage - 1 : 0;
        void this.renderPage(this.pages[pageIndex], this.getViewConfig(pageIndex));
      }
      this.handlePolygonVisibility();
    }
  }

  private drawingPolygons = new BehaviorSubject<{ [key: number]: L.FeatureGroup }>({});
  public position = new BehaviorSubject<boolean>(false);
  private _polygons: QPointPagedMultiPolygon[];
  @Input() set polygons(data: QPointPagedMultiPolygon[]) {
    if (this.manuallyUpdatedPolygons) {
      this.manuallyUpdatedPolygons = false;
      return;
    }
    this._polygons = data;
    if (this.map) {
      this.clearPolygons();
      this.addInitialPolygons();
    }
  }

  @Input() zoomInButtonTooltip: string = null;
  @Input() zoomOutButtonTooltip: string = null;

  @Input() drawRectangleButtonTooltip: string = null;
  @Input() drawPolygonButtonTooltip: string = null;

  @Input() editButtonTooltip: string = null;
  @Input() dragButtonTooltip: string = null;
  @Input() cutButtonTooltip: string = null;
  @Input() removeButtonTooltip: string = null;

  @Input() popupLabelCircumference: string = 'Umfang';
  @Input() popupLabelArea: string = 'Fläche';
  @Input() popupScalingFactorRequiredText: string = 'Zur Berechnung des Umfangs und der Fläche ist der Zeichnungsmaßstab festzulegen.';

  @Input() controlTextConfig: ControlTextConfig = {
    controlLayersTitle: 'Ebenen',
    controlLayerAllLayersToggle: 'Alle Ebenen'
  };

  // Hardcoded german as default value, will be provided by applications anyway
  @Input() scaleDialogTextConfig: ScaleDialogTextConfig = {
    title: 'Zeichnungs-Maßstab festlegen',
    referenceLengthLabel: 'Referenz Länge',
    referenceLengthButton: 'Aus Plan messen',
    realLengthLabel: 'Reale Länge',
    cancelButton: 'Abbrechen',
    confirmButton: 'Übernehmen',
    realLengthUnit: 'm',
    referenceLengthUnit: 'Pixel'
  };

  @Input() set scaleData(data: ScaleDialogData) {
    this.setScale(data);
  }

  // Unique identifier of the entity the component shows; used as part of the id to store settings in local storage
  @Input() entityId: string | null = null;

  /**
   * LocalStorageKey should be set when initializing and can not be updated.
   * Only provide a unique identifier for the user, distinction between files is handled by the component
   */
  @Input() localStorageKey: string | null = null;

  @Input() set undoDisabled(disabled: boolean) {
    this._undoDisabled = disabled;
    this.setToolbarButtonDisabled('undo', disabled);
  }

  @Input() set redoDisabled(disabled: boolean) {
    this._redoDisabled = disabled;
    this.setToolbarButtonDisabled('redo', disabled);
  }

  /**
   * ZoomGranularity should be set when initializing, updates to this variable will not be recognized while the component is instantiated
   **/
  @Input() zoomGranularity = DEFAULT_ZOOM_GRANULARITY;

  @Output() polygonsChanged = new EventEmitter<QPointPagedMultiPolygon[]>();
  @Output() areaCalculated = new EventEmitter<number>();
  @Output() vertexRightClicked = new EventEmitter<DrawingRightClickEvent>();
  @Output() polygonRightClicked = new EventEmitter<DrawingPolygonRightClickEvent>();
  @Output() mapRightClicked = new EventEmitter<DrawingRightClickEvent>();
  @Output() scaleChanged = new EventEmitter<ScaleDialogData>();
  @Output() undoClicked = new EventEmitter();
  @Output() redoClicked = new EventEmitter();

  @HostBinding('style.height') hostHeight = '600px';

  private popupRef: PopupRef | null = null;

  @HostListener('document:click', ['$event'])
  public documentClick(event: PointerEvent): void {
    if (this.popupRef && !this.isClickOnOpenPopup(event)) {
      this.checkAndClosePopupRef(true);
    }
  }

  @HostListener('window:resize')
  onResize() {
    if (this.popupRef) {
      this.checkAndClosePopupRef(true);
      this.checkPositionChanges(this.cbAfterOffsetChanges);
    }
  }

  @HostListener('document:keydown.escape') handleEscapeDown() {
    this.handleScaleCancel();
    this.handleDrawPolygonCancel();
  }

  constructor(private popupService: PopupService) {
    const pdfWorkerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.16.105/pdf.worker.min.js`;
    GlobalWorkerOptions.workerSrc = pdfWorkerSrc;

    combineLatest([this.drawingPolygons.asObservable(), toObservable(this.scale).pipe(
      startWith(this.scale())
    )])
      .pipe(
        distinctUntilChanged(),
        map(([polygons]) => polygons),
        takeUntilDestroyed(),
        debounceTime(200))
      .subscribe((drawingPolygons: { [key: number]: L.FeatureGroup }) => {
        let area = 0;
        const pageIndicesWithPolygons = [];
        for (let i = 0; i < this.numOfPages(); i++) {
          if (drawingPolygons[i].getLayers().length > 0) {
            pageIndicesWithPolygons.push(i);
          }
          for (const layer of drawingPolygons[i].getLayers()) {

            const scalingFactor = this.scalingFactor();
            const latLngs: Array<Array<LatLng>> = (layer as any).getLatLngs();

            const scaledCoordinates = latLngs.map(latLngArr => {
              return latLngArr.map(latLng => {
                return new L.LatLng(latLng.lat * scalingFactor, latLng.lng * scalingFactor);
              });
            });

            const turfPolygons: [number, number][][] = scaledCoordinates.map(latLngArr => {
              return latLngArr.map(latLng => {
                return [latLng.lng, latLng.lat];
              });
            });

            const areas = turfPolygons.map(latLng => {
              return this.calculatePolygonArea(latLng);
            });

            area += areas[0] - sum(areas.slice(1));
          }
        }

        const numOfPages = this.numOfPages();
        const mappedPages: { hasPolygon: boolean, index: number }[] = [];

        for (let i = 0; i < numOfPages; i++) {
          mappedPages.push({
            index: i,
            hasPolygon: pageIndicesWithPolygons.includes(i)
          });
        }

        this.mappedPages.set(mappedPages);

        this.areaCalculated.emit(area);
      });


  }

  async ngOnDestroy() {
    this.createMapTrigger$.next();
    this.createMapTrigger$.complete();
    if (this.resizeObserver) {
      this.resizeObserver.unobserve(this.mapRef.nativeElement);
      this.resizeObserver.disconnect();
    }
  }

  async ngAfterViewInit() {
    this.createMap();
    void this.loadFileForLeaflet();
    this.initialized = true;

    L.DomEvent.disableScrollPropagation(this.scaleDialogComponent.elementRef.nativeElement);
    L.DomEvent.disableClickPropagation(this.scaleDialogComponent.elementRef.nativeElement);

    this.resizeObserver = new ResizeObserver(() => {
      this.position.next(true);
    });

    this.resizeObserver.observe(this.mapRef.nativeElement);
    this.position.pipe(debounceTime(100)).subscribe(() => {
      this.checkPositionChanges(this.cbAfterOffsetChanges);
    });
  }

  checkPositionChanges(cbs: CallableFunction[] = []) {
    const currentOffsetTop = this.mapRef.nativeElement.offsetTop - window.scrollY;
    const currentOffsetLeft = this.mapRef.nativeElement.offsetLeft - window.scrollX;

    if (
      this.lastOffsetTop !== null && this.lastOffsetTop !== undefined &&
      this.lastOffsetLeft !== null && this.lastOffsetLeft !== undefined
    ) {
      // Check if the offsets have changed and popupRef is available
      if ((currentOffsetTop !== this.lastOffsetTop || currentOffsetLeft !== this.lastOffsetLeft)) {
        this.lastOffsetTop = currentOffsetTop;
        this.lastOffsetLeft = currentOffsetLeft;
        for (const cb of cbs) {
          cb();
        }
      }
    } else {
      this.lastOffsetTop = currentOffsetTop;
      this.lastOffsetLeft = currentOffsetLeft;
    }
  }

  private isClickOnOpenPopup(event: MouseEvent): boolean {
    return this.popupRef?.content?.location?.nativeElement?.contains(event.target);
  }

  private createMap() {
    this.createMapTrigger$.next();
    L.Map.addInitHook('addHandler', 'gestureHandling', GestureHandling);
    const granularity = Number.isNaN(this.zoomGranularity) || this.zoomGranularity === 0 ? DEFAULT_ZOOM_GRANULARITY : this.zoomGranularity;

    const customCRS = L.extend({}, L.CRS.Simple, {
      scale: function (zoom) {
        // Custom zoom scaling, more granular steps
        return Math.pow(2, zoom / granularity); // Adjust the division factor for finer zoom
      },
      zoom: function (scale) {
        // Inverse of the scale function for finer zoom control
        return Math.log(scale) / Math.LN2 * granularity; // Adjust accordingly
      }
    });

    this.map = (L as any).map(this.mapRef.nativeElement, {
      crs: customCRS,
      center: [0, 0],
      zoom: 0,
      minZoom: -2,
      maxZoom: 5,
      pmIgnore: false,
      trackResize: true,
      wheelPxPerZoomLevel: 90,
      attributionControl: false,
      dragging: !this._isDrawingEnabled
    });

    this.map.pm.setGlobalOptions({
      tooltips: false,
      snappable: false
    });

    this.map.zoomControl.remove();
    L.control.zoom({
      zoomInTitle: this.zoomInButtonTooltip,
      zoomOutTitle: this.zoomOutButtonTooltip,
      position: 'bottomright'
    }).addTo(this.map);

    // react to polygon  been created
    this.map.on('pm:create', (e) => {
      if (e.shape === 'Line') {
        return;
      }

      this.addLayerEvents(e.layer);
      const drawingPolygons = this.drawingPolygons.value;
      drawingPolygons[this.selectedIndex()].addLayer(e.layer);
      this.drawingPolygons.next(drawingPolygons);
      this.handlePolygonVisibility();
      this.polygonsChanged.emit(this.getCompleteGeoJson());
    });

    this.map.on('pm:cut', (e) => {
      this.addLayerEvents(e.layer);
      const drawingPolygons = this.drawingPolygons.value;
      drawingPolygons[this.selectedIndex()].addLayer(e.layer);
      drawingPolygons[this.selectedIndex()].removeLayer(e.originalLayer);
      this.drawingPolygons.next(drawingPolygons);
    });

    this.map.on('contextmenu', (e) => {
      const absoluteCoords = this.getAbsoluteCoords(e.containerPoint);
      this.mapRightClicked.emit({
        x: absoluteCoords.x,
        y: absoluteCoords.y
      } as DrawingRightClickEvent);
    });

    this.map.on('zoomend', () => {
      this.updateViewConfig(this.selectedIndex(), this.map.getZoom(), this.map.getCenter());
    });

    this.map.on('moveend', () => {
      this.updateViewConfig(this.selectedIndex(), this.map.getZoom(), this.map.getCenter());
    });

    this.map.on('click', e => {
      if (this.elementClicked || this.isClickOnOpenPopup(e.originalEvent)) {
        this.elementClicked = false;
        return;
      }
      if (this.selectedVertex()) {
        this.deselectVertex();
      }
      if (this.selectedPolygon()) {
        this.deselectPolygon();
      }
    });

    this.map.on('pm:globaleditmodetoggled', e => {
      if (e.enabled) {
        this.disableAllExceptSelectedPolygons();

        if (this.map.dragging.enabled()) {
          this.map.dragging.disable(); // Disable dragging when edit mode is enabled
          this.getPanToolbarButton()?.toggle(false);
        }
      }
    });

    const spaceKeyDown$ = fromEvent(document, 'keydown').pipe(
      filter((e: KeyboardEvent) => e.code === 'Space'),
      map(() => true)
    );

    const spaceKeyUp$ = fromEvent(document, 'keyup').pipe(
      filter((e: KeyboardEvent) => e.code === 'Space'),
      map(() => false)
    );

    // Merge keydown and keyup into a single stream, only emit when the state changes
    const spaceKeyToggle$ = merge(spaceKeyDown$, spaceKeyUp$).pipe(
      auditTime(50),
      distinctUntilChanged()
    );

    spaceKeyToggle$
      .pipe(
        distinctUntilChanged(),
        takeUntil(this.createMapTrigger$),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((isSpacePressed) => {
        if (this.map.pm.globalEditModeEnabled()) {
          if (isSpacePressed) {
            this.map.dragging.enable();  // Enable dragging while holding space bar
          } else {
            this.map.dragging.disable(); // Disable dragging when space bar is released
          }
        }
      });
  }

  private handleControlVisibility() {

    if (this._isDrawingEnabled && this.map && !this.map?.pm.controlsVisible()) {
      // if (!this.calibrated) {
      //   this.addCalibrationControl();
      // } else {
      this.addDrawControl();
      // }
    } else if (!this._isDrawingEnabled && this.map?.pm.controlsVisible()) {
      // hide controls when not in edit mode
      this.map.pm.toggleControls();
    }
  }

  private addDrawControl() {
    this.map.pm.addControls({
      position: 'topleft',
      oneBlock: true,
      drawCircleMarker: false,
      rotateMode: false,
      drawCircle: false,
      drawMarker: false,
      drawPolyline: false,
      drawRectangle: false,
      drawPolygon: true,
      drawText: false,
      editMode: true, // true to show 'edit layer' button
      dragMode: false, // true to show 'drag layer' button
      cutPolygon: false, // true to show 'cut layer' button
      removalMode: false, // true to show 'remove layer' button
    });

    if (this.drawRectangleButtonTooltip) {
      this.map.pm.Toolbar['buttons'].drawRectangle._button.title = this.drawRectangleButtonTooltip;
    }
    if (this.drawPolygonButtonTooltip) {
      this.map.pm.Toolbar['buttons'].drawPolygon._button.title = this.drawPolygonButtonTooltip;
    }
    if (this.editButtonTooltip) {
      this.map.pm.Toolbar['buttons'].editMode._button.title = this.editButtonTooltip;
    }
    if (this.dragButtonTooltip) {
      this.map.pm.Toolbar['buttons'].dragMode._button.title = this.dragButtonTooltip;
    }
    if (this.cutButtonTooltip) {
      this.map.pm.Toolbar['buttons'].cutPolygon._button.title = this.cutButtonTooltip;
    }
    if (this.removeButtonTooltip) {
      this.map.pm.Toolbar['buttons'].removalMode._button.title = this.removeButtonTooltip;
    }

    if (!(this.map.pm.Toolbar as any).buttons.pan) {
      this.map.pm.Toolbar.createCustomControl(getPanControlOptions(() => this.toggleMapPan()));
    }

    if (!(this.map.pm.Toolbar as any).buttons.scale) {
      this.map.pm.Toolbar.createCustomControl(getScaleControlOptions(() => this.toggleScaleWindow()));
    }

    if (!(this.map.pm.Toolbar as any).buttons.undo) {
      this.map.pm.Toolbar.createCustomControl(getUndoControlOptions(() => this.undoLastAction()));
    }

    if (!(this.map.pm.Toolbar as any).buttons.redo) {
      this.map.pm.Toolbar.createCustomControl(getRedoControlOptions(() => this.redoLastAction()));
    }

    this.map.pm.Toolbar.changeActionsOfControl('editMode', []);
    this.map.pm.Toolbar.changeActionsOfControl('drawPolygon', []);
    this.map.pm.Toolbar.changeControlOrder(['editMode', 'pan', 'scale', 'drawPolygon']);

    // refresh buttons by toggeling visibilty two times
    this.map.pm.toggleControls();
    this.map.pm.toggleControls();

    this.replaceNativeToolbarButtonIcon('drawPolygon', faDrawPolygon);
    this.replaceNativeToolbarButtonIcon('editMode', faArrowPointer);
    this.replaceNativeToolbarButtonIcon('scale', faRulerVertical);
    this.replaceNativeToolbarButtonIcon('undo', faUndo);
    this.replaceNativeToolbarButtonIcon('redo', faRedo);
    this.replaceNativeToolbarButtonIcon('pan', faHand);
    this.setToolbarButtonDisabled('undo', this._undoDisabled);
    this.setToolbarButtonDisabled('redo', this._redoDisabled);
  }

  async loadFileForLeaflet() {
    this.currentPdfDoc = undefined;
    if (this.type === 'pdf') {
      getDocument(this._src).promise.then(async (doc) => {
        this.currentPdfDoc = doc;
        const docSettings = this.getLocalStorageData();
        this.viewConfigs = this.getLocalStorageData().viewConfigs ? new Map(JSON.parse(this.getLocalStorageData().viewConfigs)) : new Map();
        this.selectedIndex.set(docSettings.activeIndex);
        // load all pages
        const promises: Promise<PDFPageProxy>[] = [];
        for (let i = 1; i <= doc.numPages; i++) {
          promises.push(doc.getPage(i));
        }
        const optionalContentConfig = await doc.getOptionalContentConfig();
        this.optionalConfig = optionalContentConfig;

        if (optionalContentConfig && docSettings.pdfLayerGroups) {
          Object.keys((docSettings.pdfLayerGroups || {})).forEach(key => {
            optionalContentConfig.setVisibility(key, docSettings.pdfLayerGroups[key].visible ?? true);
          });
        }

        this.layers.set(optionalContentConfig.getGroups());

        Promise.all(promises)
          .then(pages => {
            this.pages = pages;
            this.numOfPages.set(pages.length);
            const map = this.map;
            map.pm.setLang(this.language);

            const controls: L.Control[] = [new ControlFullscreen()];

            if (Object.keys(this.layers() || {}).length > 0) {
              controls.push(new ControlPdfLayer({elementId: this.controlPdfLayerId}));
            }

            const controlOpacity = new ControlOpacity({opacity: docSettings.opacity});
            controlOpacity.valueChanged = (opacity) => this.updateLocalStorage({opacity});
            controls.push(controlOpacity);

            if (pages.length > 1) {
              const controlPagination = new ControlPagination({
                activeIndex: this.selectedIndex(),
                pages: this.numOfPages(),
                elementId: this.controlPaginationId
              });
              controls.push(
                controlPagination
              );
              this.paginationUpdateEffect?.destroy();

              runInInjectionContext(this.injector, () => {
                this.paginationUpdateEffect = effect(() => {
                  const activeIndex = this.selectedIndex();
                  controlPagination.updateIndex(activeIndex + 1);
                });
              });
            }

            if (this.topRightControlBar) {
              this.map.removeControl(this.topRightControlBar);
            }
            this.topRightControlBar = new ControlBar({position: 'topright', controls});
            this.topRightControlBar.addTo(this.map);

            const drawingPolygons = {};
            for (let i = 0; i < pages.length; i++) {
              drawingPolygons[i] = new L.FeatureGroup();
            }
            this.drawingPolygons.next(drawingPolygons);

            const pageIndex = this._initialPage ? this._initialPage - 1 : docSettings.activeIndex;
            if (this.map?.pm.controlsVisible()) {
              /* hide controls before rendering pages, because custom icons become invisible */
              this.map.pm.toggleControls();
            }
            this.renderPage(pages[pageIndex], this.getViewConfig(pageIndex))
              .then(() => {
                this.clearPolygons();
                this.addInitialPolygons();
                setTimeout(() => {
                  /* delay enabling buttons, otherwise custom icons are not rendered */
                  this.handleControlVisibility();
                }, 500);
              });
          });
      });
    }
  }

  private clearPolygons() {
    const layers = L.PM.Utils.findLayers(this.map);
    layers.forEach(layer => {
      if (!layer['_url']) {
        layer.remove();
      }
    });
    const drawingPolygons = this.drawingPolygons.value;
    for (let i = 0; i < this.numOfPages(); i++) {
      drawingPolygons[i].clearLayers();
    }
    this.drawingPolygons.next(drawingPolygons);
  }

  private addInitialPolygons() {
    if (!this._polygons || this._polygons.length < 1 || !this._polygons.some(p => p.multiPolygon.coordinates !== null && p.multiPolygon.coordinates.length > 0)) {
      return;
    }

    const drawingPolygons = this.drawingPolygons.value;
    for (let page = 0; page < this._polygons.length; page++) {
      const polyLayers = [];
      for (const feature of this._polygons[page].multiPolygon.coordinates) {
        polyLayers.push(L.polygon(feature));
      }

      for (const layer of polyLayers) {
        this.addLayerEvents(layer);
        drawingPolygons[this._polygons[page].page - 1].addLayer(layer);
      }
    }

    this.drawingPolygons.next(drawingPolygons);
    this.handlePolygonVisibility();
  }


  public deleteSelectedVertex() {
    (L.PM.Utils as any)._fireEvent(this.selectedVertex(), 'contextmenu', {definitelyDelete: true});
  }

  selectVertex(vertex) {
    if (this.selectedVertex()) {
      this.deselectVertex();
    }
    const icon = vertex.options.icon;
    icon.options.className = 'marker-icon active';
    vertex.setIcon(icon);
    this.selectedVertex.set(vertex);
  }

  deselectVertex() {
    const icon = this.selectedVertex().options.icon;
    icon.options.className = 'marker-icon';
    this.selectedVertex().setIcon(icon);
    this.selectedVertex.set(null);
  }

  selectPolygon(polygon: L.Polygon) {
    if (this.selectedPolygon()) {
      this.deselectPolygon();
    }
    if (this._isDrawingEnabled) {
      polygon.pm.enable();
    }
    polygon.setStyle({fillColor: 'url(#diagonalHatch)', fillOpacity: 0.7});
    const backgroundOpacity = this.getLocalStorageData().opacity;
    this.map.eachLayer((layer: any) => {
      if (layer instanceof L.ImageOverlay) {
        layer.setOpacity(Math.max(0.1, backgroundOpacity - 0.2));
      }
    });
    this.selectedPolygon.set(polygon);
  }

  deselectPolygon() {
    this.selectedPolygon().pm.disable();
    this.selectedPolygon().setStyle({fillColor: '#8993F8'});
    const backgroundOpacity = this.getLocalStorageData().opacity;
    this.map.eachLayer((layer: any) => {
      if (layer instanceof L.ImageOverlay) {
        layer.setOpacity(backgroundOpacity);
      }
    });
    this.selectedPolygon.set(null);
    this.checkAndClosePopupRef(false);
  }

  closePopupRef() {
    this.checkAndClosePopupRef(false);
  }

  private showVirtualVertices(show = true) {
    const virtualVertices = document.getElementsByClassName('marker-icon-middle');
    for (let i = 0; i < virtualVertices.length; i++) {
      if (show) {
        virtualVertices[i].classList.remove('hidden');
      } else {
        virtualVertices[i].classList.add('hidden');
      }
    }
  }

  private addEventListenersToVirtualVertices() {
    const virtualVertices = document.getElementsByClassName('marker-icon-middle');
    for (let i = 0; i < virtualVertices.length; i++) {
      virtualVertices[i].addEventListener('mouseover', () => {
        this.mouseCursorOnVirtualVertex = true;
        this.showVirtualVertices();
      });
      virtualVertices[i].addEventListener('mouseout', () => {
        this.mouseCursorOnVirtualVertex = false;
        if (!this.mouseCursorOnPolygon && !this.mouseCursorOnVirtualVertex) {
          this.showVirtualVertices(false);
        }
      });
    }
  }

  private addLayerEvents(layer) {
    layer.on('pm:edit', () => {
      this.manuallyUpdatedPolygons = true;
      this.drawingPolygons.next(this.drawingPolygons.value);
      this.checkAndClosePopupRef(false);
      this.openPolygonPopupEditMode(layer);
      this.polygonsChanged.emit(this.getCompleteGeoJson());
    });

    layer.on('pm:markerdragend', () => {
      this.selectPolygon(layer);
      this.mouseCursorOnVirtualVertex = false;
      this.showVirtualVertices(false);
    });

    layer.on('pm:vertexclick', (e) => {
      this.elementClicked = true;
      this.selectVertex(e.markerEvent.target);
    });

    layer.on('pm:vertexremoved', () => {
      this.showVirtualVertices(false);
    });

    layer.on('mouseover', () => {
      if (this.selectedPolygon() === layer) {
        this.mouseCursorOnPolygon = true;
        this.showVirtualVertices();
        this.addEventListenersToVirtualVertices();
      }
    });

    layer.on('mouseout', () => {
      if (this.selectedPolygon() === layer) {
        this.mouseCursorOnPolygon = false;
        if (!this.mouseCursorOnPolygon && !this.mouseCursorOnVirtualVertex) {
          this.showVirtualVertices(false);
        }
      }
    });

    (layer as L.Polygon).pm.setOptions({
      removeVertexValidation: (e: any) => {
        if (e.marker.leftM && e.marker.rightM) {
          // no context menu for virtual vertices
          return false;
        }
        if (e.event.definitelyDelete) {
          return true;
        } else {
          this.selectVertex(e.marker);
          const absoluteCoords = this.getAbsoluteCoords(e.event.containerPoint);
          this.vertexRightClicked.emit({
            x: absoluteCoords.x,
            y: absoluteCoords.y,
          });
          return false;
        }
      }
    });

    layer.setStyle({fillColor: '#8993F8', fillOpacity: 0.5});

    layer.on('click', () => {
      this.elementClicked = true;
      this.selectPolygon(layer);
    });

    layer.on('pm:remove', e => {
      const drawingPolygons = this.drawingPolygons.value;
      drawingPolygons[this.selectedIndex()].removeLayer(e.layer);
      this.drawingPolygons.next(drawingPolygons);
      this.polygonsChanged.emit(this.getCompleteGeoJson());
    });

    layer.on('click', (e: L.LeafletMouseEvent) => {
      if (this._isDrawingEnabled) {
        this.openPolygonPopupEditMode(layer);
      } else {
        this.openPolygonPopupViewMode(e, layer);
      }
    });

    layer.on('contextmenu', (e: L.LeafletMouseEvent) => {
      const absoluteCoords = this.getAbsoluteCoords(e.containerPoint);
      this.polygonRightClicked.emit({
        polygonId: e.target._leaflet_id,
        x: absoluteCoords.x,
        y: absoluteCoords.y
      } as DrawingPolygonRightClickEvent);
      e.target.options.bubblingMouseEvents = false;
    });
  }

  private getAbsoluteCoords(containerPoint: L.Point) {
    const mapContainerRect = this.mapRef.nativeElement.getBoundingClientRect();
    return {
      x: containerPoint.x + mapContainerRect.left - window.scrollX,
      y: containerPoint.y + mapContainerRect.top - window.scrollY
    };
  }

  private getCompleteGeoJson(): QPointPagedMultiPolygon[] {
    const multiPolygons: QPointPagedMultiPolygon[] = [];

    for (let pageIndex = 0; pageIndex < this.numOfPages(); pageIndex++) {
      const layers = this.drawingPolygons.value[pageIndex].getLayers();
      const lg = new L.LayerGroup();
      layers.forEach(layer => {
        lg.addLayer(layer);
      });
      const completeGeoJson = lg.toGeoJSON();
      const coordinates: [number, number][][][] = [];
      for (let i = 0; i < completeGeoJson['features'].length; i++) {
        const leafletFeatures = completeGeoJson['features'][i]['geometry']['coordinates'];
        const geoJsonFeatures: any[] = [];

        // Leaflet uses lat-lng (or northing-easting) whereas GeoJSON uses lng-lat (or easting-northing)
        leafletFeatures.forEach(leafletCoordinates => {
          const geoJsonCoordinates: any[] = [];

          leafletCoordinates.forEach(leafletCoordinate => {
            geoJsonCoordinates.push([leafletCoordinate[1], leafletCoordinate[0]]);
          });
          geoJsonFeatures.push(geoJsonCoordinates);
        });

        coordinates.push(geoJsonFeatures);

      }

      if (coordinates.length > 0) {
        multiPolygons.push(new QPointPagedMultiPolygon({
          type: 'MultiPolygon',
          coordinates: coordinates
        }, pageIndex + 1));
      }
    }

    return multiPolygons;
  }

  private checkAndClosePopupRef(onlyViewMode: boolean = false) {
    /* within edit mode, popup should only be closed when deselecting polygon */
    if (this.popupRef && (!onlyViewMode || !this._isDrawingEnabled)) {
      this.popupRef.close();
    }
  }

  private convertLayerToVertices(layer: L.Layer) {
    const latLngs: Array<Array<LatLng>> = (layer as any).getLatLngs();

    const scalingFactor = this.scalingFactor();
    const scaledCoordinates = latLngs.map(latLngArr => {
      return latLngArr.map(latLng => {
        return new L.LatLng(latLng.lat * scalingFactor, latLng.lng * scalingFactor);
      });
    });

    const turfPolygons: [number, number][][] = scaledCoordinates.map(latLngArr => {
      return latLngArr.map(latLng => {
        return [latLng.lng, latLng.lat];
      });
    });

    return turfPolygons[0];
  }

  private openPolygonPopupViewMode(e: L.LeafletMouseEvent, layer: L.Layer) {
    const popupRef = this.popupService.open({
      content: QPointPolygonPopUpComponent,
      offset: {
        top: e.containerPoint.y,
        left: e.containerPoint.x
      },
      popupAlign: {
        horizontal: 'right',
        vertical: 'center'
      },
      collision: {
        horizontal: 'flip',
        vertical: 'flip'
      },
      appendTo: this.mapContainerRef,
      positionMode: 'absolute'
    });
    this.openPolygonPopup(popupRef, layer);
  }

  private openPolygonPopupEditMode(layer: L.Layer) {
    const popupRef = this.popupService.open({
      content: QPointPolygonPopUpComponent,
      animate: false,
      offset: {
        top: 10,
        left: 60
      },
      popupAlign: {
        horizontal: 'left',
        vertical: 'top'
      },
      collision: {
        horizontal: 'flip',
        vertical: 'flip'
      },
      appendTo: this.mapContainerRef,
      positionMode: 'absolute'
    });
    this.openPolygonPopup(popupRef, layer);
  }

  private openPolygonPopup(popupRef: PopupRef, layer: L.Layer) {
    this.hideScaleDialog();
    this.mapRef.nativeElement.appendChild(popupRef.popupElement);

    const vertices: [number, number][] = this.convertLayerToVertices(layer);

    const instance: QPointPolygonPopUpComponent = popupRef.content.instance;
    instance.scalingFactor = this.scalingFactor();
    instance.scalingFactorRequiredText = this.popupScalingFactorRequiredText;
    instance.scalingFactorRequiredTemplate = this.scalingFactorRequiredTemplate;
    instance.circumferenceLabel = this.popupLabelCircumference;
    instance.circumference = this.calculatePolygonCircumference(vertices);
    instance.areaLabel = this.popupLabelArea;
    instance.area = this.calculatePolygonArea(vertices);

    setTimeout(() => {
      this.popupRef = popupRef;
    }, 0);
  }

  private hideScaleDialog() {
    if (this.scaleDialogComponent.display() == true) {
      this.getScaleToolbarButton().toggle();
      this.scaleDialogComponent.setDisplay(false);
    }
  }

  calculatePolygonCircumference(vertices: [number, number][]) {
    let circumference = 0;

    // Iterate through each vertex
    for (let i = 0; i < vertices.length; i++) {
      const [lat1, lng1] = vertices[i];
      const [lat2, lng2] = vertices[(i + 1) % vertices.length]; // Wrap around for the last vertex

      // Calculate the distance between the two points
      const distance = Math.sqrt((lat2 - lat1) ** 2 + (lng2 - lng1) ** 2);

      circumference += distance;
    }

    return circumference;
  }

  calculatePolygonArea(vertices: [number, number][]) {
    let area = 0;

    // Iterate through each vertex
    for (let i = 0; i < vertices.length; i++) {
      const [lat1, lng1] = vertices[i];
      const [lat2, lng2] = vertices[(i + 1) % vertices.length]; // Wrap around for the last vertex

      area += lat1 * lng2 - lat2 * lng1;
    }

    // Take the absolute value and divide by 2
    area = Math.abs(area) / 2;

    return area;
  }

  public showPage(page: number) {
    /* store config of previous page */
    this.updateViewConfig(this.selectedIndex(), this.map.getZoom(), this.map.getCenter());
    this.selectedIndex.set(page);
    this.updateLocalStorage({activeIndex: page});
    const pageProxy = this.pages[page];
    this.renderPage(pageProxy, this.getViewConfig(page));
    this.handlePolygonVisibility();
  }

  private handlePolygonVisibility() {
    this.handleControlVisibility();
    const pagesToHide = range(this.numOfPages());
    pagesToHide.splice(this.selectedIndex(), 1);
    pagesToHide.forEach(page => {
      this.map.removeLayer(this.drawingPolygons.value[page]);
    });
    this.map.addLayer(this.drawingPolygons.value[this.selectedIndex()]);
  }

  private async renderPage(page: PDFPageProxy, viewConfig: ViewConfig, keepBounds = false) {
    this.loading.set(true);
    const viewport = page.getViewport({scale: this.pdfScale});
    const southWest = this.map.unproject([0, viewport.height], 4);
    const northEast = this.map.unproject([viewport.width, 0], 4);
    const bounds: LatLngBoundsExpression = new L.LatLngBounds(southWest, northEast);

    return convertPdfPageToDataURL(page, this.pdfScale, {optionalContentConfigPromise: Promise.resolve(this.optionalConfig)})
      .then(
        dataUrl => {
          let opacity = this.getLocalStorageData().opacity;
          this.map.eachLayer(layer => {
            if (layer instanceof L.ImageOverlay) {
              opacity = layer.options.opacity || 1;
              this.map.removeLayer(layer);
            }
          });
          if (!keepBounds) {
            this.map.setMaxBounds(bounds);
            // fitBounds triggers a weird "jump" animation when executed, keep code for now
            // this.map.fitBounds(bounds);
          }

          if (viewConfig) {
            setTimeout(() => {
              /* should be performed after page update has been finished */
              this.map.setView(viewConfig.center, viewConfig.zoom, {animate: false, duration: 0});
            }, 500);
          }

          setTimeout(() => {
            L.imageOverlay(createPdfPageBackdrop(page, this.pdfScale), bounds, {className: 'qpoint-pdf-backdrop'}).addTo(this.map);
            L.imageOverlay(dataUrl, bounds, {opacity}).addTo(this.map);
          });

          this.cdr.markForCheck();
          this.loading.set(false);
        }
      );
  }

  setLayerVisibility(key: string, visible: boolean) {
    this.optionalConfig.setVisibility(key, visible);
    this.layers.set(this.optionalConfig.getGroups());
    this.rerenderPage();
    this.updatePdfLayerVisibilityLocalStorage(key, visible);
  }

  setLayerVisbilityAll() {
    const groups = this.layers();
    const allVisible = Object.values(groups).every(group => group.visible);
    const pdfLayerGroups = {};
    Object.keys(groups).forEach(key => {
      this.optionalConfig.setVisibility(key, !allVisible);
      pdfLayerGroups[key] = {visible: !allVisible};
    });

    this.layers.set(this.optionalConfig.getGroups());
    this.rerenderPage();

    this.updateLocalStorage({pdfLayerGroups});
  }

  rerenderPage() {
    void this.renderPage(this.pages[this.selectedIndex()], this.getViewConfig(this.selectedIndex()), true);
  }

  deletePolygon(polygonId: number) {
    const drawingPolygons = this.drawingPolygons.value;
    drawingPolygons[this.selectedIndex()].removeLayer(polygonId);
    this.drawingPolygons.next(drawingPolygons);
    this.checkAndClosePopupRef(false);
    this.polygonsChanged.emit(this.getCompleteGeoJson());
  }

  handleScaleMeasure() {
    this.map.pm.setGlobalOptions({
      cursorMarker: false,
      hideMiddleMarkers: true
    });
    if (this.scaleLineLayer) {
      this.scaleLineLayer.remove();
    }

    this.map.once('pm:create', e => {
      this.scaleLineLayer = e.layer;
      (e.layer as L.Polyline).setStyle({dashArray: [3, 3], lineCap: 'butt', weight: 2});
      (this.scaleLineLayer as any).pm.enable({preventMarkerRemoval: true});

      e.layer.once('pm:disable', e => {
        e.layer.off();
      });

      e.layer.on('pm:edit', e => {
        const latLngs = (e.layer as any).getLatLngs();
        this.scaleDialogComponent.calculateAndSetEuclideanDistance(latLngs);
      });
    });

    this.map.once('pm:drawstart', e => {
      e.workingLayer.on('pm:vertexadded', e => {
        const latLngs = (e.workingLayer as any).getLatLngs();
        if (latLngs.length === 2) {
          e.workingLayer.off();
          (this.map.pm.Draw as any).Line._finishShape();
          this.scaleDialogComponent.setMeasuring(false);
          this.scaleDialogComponent.calculateAndSetEuclideanDistance(latLngs);

          this.map.pm.setGlobalOptions({
            cursorMarker: true,
            hideMiddleMarkers: true
          });
        }
      });
    });

    setTimeout(() => {
      if (this.scaleDialogComponent.measuring()) {
        return;
      }

      this.scaleDialogComponent.setMeasuring(true);
      (this.map.pm.Draw as any).Line.toggle();
    });
  }

  handleDrawPolygonCancel() {
    if (this.getDrawPolygonToolbarButton()._button.toggleStatus) {
      this.getDrawPolygonToolbarButton()._triggerClick();
    }
  }

  handleScaleCancel() {
    if (this.scaleLineLayer) {
      this.scaleLineLayer.remove();
    }

    if (this.scaleDialogComponent.measuring()) {
      (this.map.pm.Draw as any).Line.toggle();
      this.scaleDialogComponent.setMeasuring(false);
    }

    this.map.pm.setGlobalOptions({
      cursorMarker: true,
      hideMiddleMarkers: false
    });

    if (this.getScaleToolbarButton()._button.toggleStatus !== this.scaleDialogComponent.display()) {
      this.getScaleToolbarButton().toggle();
    }
  }

  handleScaleChange(event: ScaleDialogData) {
    this.setScale(event);
    this.scaleChanged.emit(event);
    this.scaleDialogComponent.setDisplay(!this.scaleDialogComponent.display());
    this.handleScaleCancel();
  }

  toggleScaleWindow() {
    const button = this.getScaleDomButton();
    this.scaleDialogComponent.setPosition(button.offsetLeft + 60, 60);
    this.scaleDialogComponent.setDisplay(!this.scaleDialogComponent.display());
    if (this.getScaleToolbarButton()._button.toggleStatus !== this.scaleDialogComponent.display()) {
      this.getScaleToolbarButton().toggle();
    }

    if (!this.scaleDialogComponent.display()) {
      this.handleScaleCancel();
      this.scaleDialogComponent.resetForm();
    } else {
      this.deselectPolygon();
    }
  }

  toggleMapPan() {
    const map = this.map;
    if (map.dragging.enabled()) {
      map.dragging.disable();
    } else {
      map.pm.disableGlobalEditMode();
      map.dragging.enable();
    }
  }

  setScale(scale: ScaleDialogData) {
    this._scale.set(scale);
  }

  private updatePdfLayerVisibilityLocalStorage(key: string, visible: boolean) {
    if (this.getLocalStorageKey()) {
      const localStorageData = this.getLocalStorageData();

      const pdfLayerGroups: Record<string, PdfLayerGroup> = {
        ...localStorageData.pdfLayerGroups,
        [key]: {
          visible
        }
      };

      this.updateLocalStorage({pdfLayerGroups});
    }
  }

  private getLocalStorageData(): ReturnType<typeof getDrawingLocalStorage> {
    const defaultOptions: DrawingLocalStorage = {
      opacity: 1,
      pdfLayerGroups: null,
      activeIndex: 0,
      viewConfigs: ''
    };

    if (!this.currentPdfDoc || !this.localStorageKey) {
      return defaultOptions;
    } else {
      return {
        ...defaultOptions,
        ...getDrawingLocalStorage(this.getLocalStorageKey())
      };
    }
  }

  private getLocalStorageKey(): string | null {
    if (!this.localStorageKey || !this.currentPdfDoc || !this.entityId) {
      return null;
    }

    return `${this.localStorageKey}+${this.entityId}+${this.currentPdfDoc.fingerprints[0]}`;
  }

  updateLocalStorage(partial: Partial<DrawingLocalStorage>) {
    const key = this.getLocalStorageKey();

    if (key) {
      saveDrawingLocalStorage(key, partial);
    }
  }

  private getViewConfig(page: number): ViewConfig {
    return this.viewConfigs.get(page);
  }

  private updateViewConfig(page: number, zoom: number, center: L.LatLng) {
    this.viewConfigs.set(page, {zoom, center});
    this.updateLocalStorage({viewConfigs: JSON.stringify(Array.from(this.viewConfigs))});
  }

  private getScaleToolbarButton() {
    return (this.map.pm.Toolbar as any).buttons.scale;
  }

  private getPanToolbarButton() {
    return (this.map.pm.Toolbar as any).buttons?.pan;
  }

  private getDrawPolygonToolbarButton() {
    return (this.map.pm.Toolbar as any).buttons.drawPolygon;
  }

  private getScaleDomButton(): HTMLDivElement {
    return (this.getScaleToolbarButton().buttonsDomNode as HTMLDivElement);
  }

  private getNativeToolbarButton(button: string): L.Control & { buttonsDomNode: HTMLDivElement } {
    if (!this.map?.pm?.Toolbar) {
      return null;
    }

    return this.map.pm?.Toolbar?.getButton(button) as L.Control & { buttonsDomNode: HTMLDivElement };
  }

  private replaceNativeToolbarButtonIcon(button: string, icon: IconDefinition) {
    const ctrlButton = this.getNativeToolbarButton(button);
    if (ctrlButton) {
      ctrlButton.buttonsDomNode.querySelector('.control-icon').replaceWith(getRenderedIcon(icon).node[0]);
    }
  }

  private setToolbarButtonDisabled(button: string, disabled: boolean) {
    if (!this.map?.pm?.Toolbar) {
      return;
    }

    this.map.pm.Toolbar.setButtonDisabled(button, disabled);
  }

  private undoLastAction() {
    this.undoClicked.emit();
  }

  private redoLastAction() {
    this.redoClicked.emit();
  }

  private disableAllExceptSelectedPolygons() {
    this.map.pm.getGeomanLayers().forEach(layer => {
      if (layer instanceof L.Polygon && layer !== this.selectedPolygon()) {
        layer.pm.disable();
      }
    });
  }
}
